/**
 * Related Videos Carousel Template Module
 *
 * Horizontal scrolling carousel for related video content.
 * Features video thumbnails with play button overlays, titles, and metadata.
 */

import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { TemplateModule, TemplateConfig, PreviewProps } from '../../types';

// ============================================================================
// TEMPLATE DEFINITION
// ============================================================================

const definition = {
  id: 'related-videos-carousel',
  name: 'Related Videos Carousel',
  category: 'carousel' as const,
  description: 'Horizontal scrolling carousel for related video content with play overlays',
  tags: ['Video', 'Carousel', 'Media', 'YouTube', 'Entertainment'],
  fields: [
    { name: 'showHeading', label: 'Show Heading', type: 'checkbox' as const, default: true },
    { name: 'headingText', label: 'Heading Text', type: 'text' as const, default: 'Related Videos' },
    {
      name: 'headingStyle',
      label: 'Heading Style',
      type: 'select' as const,
      options: ['simple', 'with-icon', 'minimal'],
      default: 'with-icon',
    },
    {
      name: 'cardStyle',
      label: 'Card Style',
      type: 'select' as const,
      options: ['elevated', 'flat', 'bordered'],
      default: 'elevated',
    },
    {
      name: 'cardSize',
      label: 'Card Size',
      type: 'select' as const,
      options: ['small', 'medium', 'large'],
      default: 'medium',
    },
    { name: 'visibleCards', label: 'Visible Cards', type: 'number' as const, default: 4, min: 2, max: 6 },
    { name: 'maxCards', label: 'Max Cards', type: 'number' as const, default: 10, min: 4, max: 20 },
    { name: 'showPlayButton', label: 'Show Play Button', type: 'checkbox' as const, default: true },
    {
      name: 'playButtonStyle',
      label: 'Play Button Style',
      type: 'select' as const,
      options: ['filled', 'outline', 'minimal'],
      default: 'filled',
    },
    { name: 'playButtonColor', label: 'Play Button Color', type: 'color' as const, default: '#ff0000' },
    { name: 'showDuration', label: 'Show Duration', type: 'checkbox' as const, default: true },
    { name: 'showTitle', label: 'Show Title', type: 'checkbox' as const, default: true },
    { name: 'titleLines', label: 'Title Lines', type: 'select' as const, options: ['1', '2', '3'], default: '2' },
    { name: 'showChannel', label: 'Show Channel Name', type: 'checkbox' as const, default: true },
    { name: 'showViews', label: 'Show View Count', type: 'checkbox' as const, default: true },
    { name: 'showArrows', label: 'Show Navigation Arrows', type: 'checkbox' as const, default: true },
    { name: 'showDots', label: 'Show Dot Indicators', type: 'checkbox' as const, default: false },
    { name: 'autoPlay', label: 'Auto-Scroll', type: 'checkbox' as const, default: false },
    {
      name: 'gap',
      label: 'Card Gap',
      type: 'select' as const,
      options: ['none', 'small', 'medium', 'large'],
      default: 'medium',
    },
    {
      name: 'borderRadius',
      label: 'Border Radius',
      type: 'select' as const,
      options: ['none', 'small', 'medium', 'large'],
      default: 'medium',
    },
    {
      name: 'hoverEffect',
      label: 'Hover Effect',
      type: 'select' as const,
      options: ['none', 'lift', 'scale', 'brighten'],
      default: 'lift',
    },
    { name: 'backgroundColor', label: 'Background Color', type: 'color' as const, default: '#ffffff' },
    { name: 'customCssClass', label: 'Custom CSS Class', type: 'text' as const, default: '' },
  ],
};

// ============================================================================
// SAMPLE DATA
// ============================================================================

const sampleData = {
  videos: [
    {
      title: 'How to Build a React App from Scratch',
      channel: 'CodeMaster',
      views: '1.2M',
      duration: '15:42',
      thumbnail: 0,
    },
    {
      title: 'Understanding TypeScript Generics',
      channel: 'Tech Academy',
      views: '856K',
      duration: '22:18',
      thumbnail: 1,
    },
    { title: '10 CSS Tips You Need to Know', channel: 'Design Daily', views: '2.1M', duration: '8:35', thumbnail: 2 },
    { title: 'Node.js Best Practices 2024', channel: 'Backend Basics', views: '445K', duration: '31:20', thumbnail: 3 },
    { title: 'Building APIs with Express', channel: 'CodeMaster', views: '678K', duration: '18:55', thumbnail: 4 },
    { title: 'Advanced React Patterns', channel: 'React Pro', views: '923K', duration: '45:10', thumbnail: 5 },
    { title: 'Git & GitHub for Beginners', channel: 'Tech Academy', views: '3.5M', duration: '25:00', thumbnail: 6 },
    { title: 'Docker in 100 Seconds', channel: 'Fireship', views: '4.2M', duration: '1:40', thumbnail: 7 },
  ],
};

// ============================================================================
// SHARED HELPERS
// ============================================================================

const COLORS = {
  navy: '#1e3a5f',
  textMuted: '#64748b',
};

const GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
];

const sizeMap = {
  small: { width: 180, height: 100, titleSize: '0.75rem', metaSize: '0.65rem' },
  medium: { width: 240, height: 135, titleSize: '0.85rem', metaSize: '0.7rem' },
  large: { width: 320, height: 180, titleSize: '0.95rem', metaSize: '0.75rem' },
};

const gapMap = { none: 0, small: 8, medium: 16, large: 24 };
const radiusMap = { none: 0, small: 4, medium: 8, large: 12 };

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const PlayButtonIcon: React.FC<{ style: string; color: string; size: number }> = ({ style, color, size }) => {
  if (style === 'minimal') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="white">
        <path d="M8 5v14l11-7z" />
      </svg>
    );
  }

  if (style === 'outline') {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          border: '2px solid white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.3)',
        }}
      >
        <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="white">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    );
  }

  // filled (default)
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 24 24" fill="white">
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  );
};

const VideoIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m10 9 5 3-5 3V9z" />
  </svg>
);

// ============================================================================
// PREVIEW COMPONENT
// ============================================================================

const Preview: React.FC<PreviewProps> = ({ config, isGalleryPreview = false }) => {
  const c = config;
  const carouselRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const cardSize = sizeMap[(c.cardSize as string) || 'medium'] || sizeMap.medium;
  const gap = gapMap[(c.gap as string) || 'medium'] || gapMap.medium;
  const radius = radiusMap[(c.borderRadius as string) || 'medium'] || radiusMap.medium;
  const visibleCards = (c.visibleCards as number) || 4;
  const maxCards = Math.min((c.maxCards as number) || 10, sampleData.videos.length);
  const playButtonColor = (c.playButtonColor as string) || '#ff0000';
  const backgroundColor = (c.backgroundColor as string) || '#ffffff';
  const titleLines = parseInt((c.titleLines as string) || '2');

  // Scale for gallery preview
  const scale = isGalleryPreview ? 0.5 : 1;
  const scaledWidth = cardSize.width * scale;
  const scaledHeight = cardSize.height * scale;
  const scaledGap = gap * scale;

  const getCardShadow = () => {
    switch (c.cardStyle) {
      case 'elevated':
        return '0 4px 12px rgba(0,0,0,0.15)';
      case 'bordered':
        return 'none';
      default:
        return 'none';
    }
  };

  const getCardBorder = () => {
    return c.cardStyle === 'bordered' ? '1px solid #e2e8f0' : 'none';
  };

  const getHoverTransform = () => {
    switch (c.hoverEffect) {
      case 'lift':
        return 'translateY(-4px)';
      case 'scale':
        return 'scale(1.03)';
      default:
        return 'none';
    }
  };

  const scrollCarousel = (direction: number) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: direction * (scaledWidth + scaledGap),
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box
      className={(c.customCssClass as string) || undefined}
      style={{
        backgroundColor,
        padding: isGalleryPreview ? '8px' : '16px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Header */}
      {c.showHeading && (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: isGalleryPreview ? '8px' : '16px',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {c.headingStyle === 'with-icon' && <VideoIcon size={isGalleryPreview ? 14 : 20} />}
            <Typography
              component="h4"
              style={{
                margin: 0,
                fontSize: isGalleryPreview ? '0.7rem' : c.headingStyle === 'minimal' ? '0.85rem' : '1.1rem',
                fontWeight: c.headingStyle === 'minimal' ? 500 : 600,
                color: c.headingStyle === 'minimal' ? COLORS.textMuted : COLORS.navy,
                textTransform: c.headingStyle === 'minimal' ? 'uppercase' : 'none',
                letterSpacing: c.headingStyle === 'minimal' ? '0.5px' : 'normal',
              }}
            >
              {(c.headingText as string) || 'Related Videos'}
            </Typography>
          </Box>

          {/* Navigation Arrows */}
          {c.showArrows && !isGalleryPreview && (
            <Box style={{ display: 'flex', gap: '4px' }}>
              <IconButton
                size="small"
                onClick={() => scrollCarousel(-1)}
                style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px' }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => scrollCarousel(1)}
                style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px' }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* Carousel Track */}
      <div
        ref={carouselRef}
        style={{
          display: 'flex',
          gap: scaledGap,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          paddingBottom: '8px',
          scrollbarWidth: 'thin',
        }}
      >
        {sampleData.videos.slice(0, maxCards).map((video, index) => {
          const isHovered = hoveredIndex === index;

          return (
            <Box
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                flexShrink: 0,
                width: scaledWidth,
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                transform: isHovered && c.hoverEffect !== 'none' ? getHoverTransform() : 'none',
                filter: isHovered && c.hoverEffect === 'brighten' ? 'brightness(1.1)' : 'none',
              }}
            >
              {/* Thumbnail */}
              <Box
                style={{
                  position: 'relative',
                  width: scaledWidth,
                  height: scaledHeight,
                  borderRadius: radius,
                  overflow: 'hidden',
                  background: GRADIENTS[video.thumbnail % GRADIENTS.length],
                  boxShadow: getCardShadow(),
                  border: getCardBorder(),
                }}
              >
                {/* Play Button Overlay */}
                {c.showPlayButton && (
                  <Box
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      opacity: isHovered ? 1 : 0.85,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <PlayButtonIcon
                      style={(c.playButtonStyle as string) || 'filled'}
                      color={playButtonColor}
                      size={isGalleryPreview ? 24 : 48}
                    />
                  </Box>
                )}

                {/* Duration Badge */}
                {c.showDuration && (
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      color: 'white',
                      padding: isGalleryPreview ? '1px 3px' : '2px 6px',
                      borderRadius: 3,
                      fontSize: isGalleryPreview ? '0.5rem' : '0.7rem',
                      fontWeight: 500,
                    }}
                  >
                    {video.duration}
                  </Box>
                )}
              </Box>

              {/* Video Info */}
              <Box style={{ padding: isGalleryPreview ? '4px 0' : '8px 0' }}>
                {c.showTitle && (
                  <Typography
                    style={{
                      fontSize: isGalleryPreview ? '0.55rem' : cardSize.titleSize,
                      fontWeight: 500,
                      color: COLORS.navy,
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: titleLines,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      marginBottom: isGalleryPreview ? '2px' : '4px',
                    }}
                  >
                    {video.title}
                  </Typography>
                )}

                {(c.showChannel || c.showViews) && (
                  <Typography
                    style={{
                      fontSize: isGalleryPreview ? '0.45rem' : cardSize.metaSize,
                      color: COLORS.textMuted,
                    }}
                  >
                    {c.showChannel && video.channel}
                    {c.showChannel && c.showViews && ' • '}
                    {c.showViews && `${video.views} views`}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </div>

      {/* Dot Indicators */}
      {c.showDots && !isGalleryPreview && (
        <Box style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '12px' }}>
          {Array.from({ length: Math.ceil(maxCards / visibleCards) }).map((_, i) => (
            <span
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: i === 0 ? playButtonColor : '#cbd5e1',
                cursor: 'pointer',
              }}
            />
          ))}
        </Box>
      )}

      {/* Auto-play indicator */}
      {c.autoPlay && !isGalleryPreview && (
        <Typography
          style={{
            textAlign: 'center',
            marginTop: '8px',
            fontSize: '0.75rem',
            color: COLORS.textMuted,
            fontStyle: 'italic',
          }}
        >
          ▶ Auto-scroll enabled
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// HTML GENERATORS
// ============================================================================

const generateHtml = (config: TemplateConfig): string => {
  const c = config;
  const cardSize = sizeMap[(c.cardSize as string) || 'medium'] || sizeMap.medium;
  const gap = gapMap[(c.gap as string) || 'medium'] || gapMap.medium;
  const radius = radiusMap[(c.borderRadius as string) || 'medium'] || radiusMap.medium;
  const playButtonColor = (c.playButtonColor as string) || '#ff0000';
  const backgroundColor = (c.backgroundColor as string) || '#ffffff';
  const titleLines = parseInt((c.titleLines as string) || '2');

  const cardShadow = c.cardStyle === 'elevated' ? 'box-shadow:0 4px 12px rgba(0,0,0,0.15);' : '';
  const cardBorder = c.cardStyle === 'bordered' ? 'border:1px solid #e2e8f0;' : '';

  let hoverCss = '';
  if (c.hoverEffect === 'lift') hoverCss = 'transform:translateY(-4px);';
  else if (c.hoverEffect === 'scale') hoverCss = 'transform:scale(1.03);';
  else if (c.hoverEffect === 'brighten') hoverCss = 'filter:brightness(1.1);';

  const playButton = c.showPlayButton
    ? `
    <div class="quattr-play-btn" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:48px;height:48px;border-radius:50%;background:${playButtonColor};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3);opacity:0.85;transition:opacity 0.2s;">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
    </div>`
    : '';

  return `<style>
.quattr-video-carousel .quattr-video-card:hover { ${hoverCss} }
.quattr-video-carousel .quattr-video-card:hover .quattr-play-btn { opacity:1; }
</style>
<div class="quattr-module quattr-video-carousel${
    c.customCssClass ? ' ' + c.customCssClass : ''
  }" data-module-id="{{moduleId}}" style="background:${backgroundColor};padding:16px;font-family:system-ui,-apple-system,sans-serif;">
  ${
    c.showHeading
      ? `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
    <div style="display:flex;align-items:center;gap:8px;">
      ${
        c.headingStyle === 'with-icon'
          ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m10 9 5 3-5 3V9z"/></svg>'
          : ''
      }
      <h4 style="margin:0;font-size:${c.headingStyle === 'minimal' ? '0.85rem' : '1.1rem'};font-weight:${
          c.headingStyle === 'minimal' ? '500' : '600'
        };color:${c.headingStyle === 'minimal' ? '#64748b' : '#1e3a5f'};${
          c.headingStyle === 'minimal' ? 'text-transform:uppercase;letter-spacing:0.5px;' : ''
        }">${c.headingText || 'Related Videos'}</h4>
    </div>
    ${
      c.showArrows
        ? `<div style="display:flex;gap:4px;">
      <button class="quattr-carousel-prev" style="border:1px solid #e2e8f0;background:white;border-radius:4px;padding:4px 8px;cursor:pointer;">←</button>
      <button class="quattr-carousel-next" style="border:1px solid #e2e8f0;background:white;border-radius:4px;padding:4px 8px;cursor:pointer;">→</button>
    </div>`
        : ''
    }
  </div>`
      : ''
  }
  <div class="quattr-carousel-track" style="display:flex;gap:${gap}px;overflow-x:auto;scroll-behavior:smooth;padding-bottom:8px;">
    {{#each videos}}
    {{#if (lt @index ${c.maxCards || 10})}}
    <div class="quattr-video-card" style="flex-shrink:0;width:${
      cardSize.width
    }px;cursor:pointer;transition:transform 0.2s,filter 0.2s;">
      <div style="position:relative;width:${cardSize.width}px;height:${
    cardSize.height
  }px;border-radius:${radius}px;overflow:hidden;${cardShadow}${cardBorder}">
        <img src="{{videoThumbnail}}" alt="{{title}}" style="width:100%;height:100%;object-fit:cover;" loading="lazy">
        ${playButton}
        ${
          c.showDuration
            ? '<div style="position:absolute;bottom:4px;right:4px;background:rgba(0,0,0,0.8);color:white;padding:2px 6px;border-radius:3px;font-size:0.7rem;font-weight:500;">{{duration}}</div>'
            : ''
        }
      </div>
      <div style="padding:8px 0;">
        ${
          c.showTitle
            ? `<div style="font-size:${cardSize.titleSize};font-weight:500;color:#1e3a5f;line-height:1.3;display:-webkit-box;-webkit-line-clamp:${titleLines};-webkit-box-orient:vertical;overflow:hidden;margin-bottom:4px;">{{title}}</div>`
            : ''
        }
        ${
          c.showChannel || c.showViews
            ? `<div style="font-size:${cardSize.metaSize};color:#64748b;">${c.showChannel ? '{{channelName}}' : ''}${
                c.showChannel && c.showViews ? ' • ' : ''
              }${c.showViews ? '{{viewCount}} views' : ''}</div>`
            : ''
        }
      </div>
    </div>
    {{/if}}
    {{/each}}
  </div>
  ${
    c.showDots
      ? '<div class="quattr-carousel-dots" style="display:flex;justify-content:center;gap:6px;margin-top:12px;"></div>'
      : ''
  }
</div>
<script>
(function(){
  var container = document.querySelector('[data-module-id="{{moduleId}}"]');
  if (!container) return;
  var track = container.querySelector('.quattr-carousel-track');
  var prevBtn = container.querySelector('.quattr-carousel-prev');
  var nextBtn = container.querySelector('.quattr-carousel-next');
  var cardWidth = ${cardSize.width} + ${gap};

  if (prevBtn) prevBtn.addEventListener('click', function() { track.scrollBy({left: -cardWidth, behavior: 'smooth'}); });
  if (nextBtn) nextBtn.addEventListener('click', function() { track.scrollBy({left: cardWidth, behavior: 'smooth'}); });

  ${c.autoPlay ? `setInterval(function() { track.scrollBy({left: cardWidth, behavior: 'smooth'}); }, 4000);` : ''}
})();
</script>`;
};

const generatePreviewHtml = (config: TemplateConfig): string => {
  const c = config;
  const cardSize = sizeMap[(c.cardSize as string) || 'medium'] || sizeMap.medium;
  const gap = gapMap[(c.gap as string) || 'medium'] || gapMap.medium;
  const radius = radiusMap[(c.borderRadius as string) || 'medium'] || radiusMap.medium;
  const playButtonColor = (c.playButtonColor as string) || '#ff0000';
  const backgroundColor = (c.backgroundColor as string) || '#ffffff';
  const titleLines = parseInt((c.titleLines as string) || '2');
  const maxCards = Math.min((c.maxCards as number) || 10, sampleData.videos.length);

  const cardShadow = c.cardStyle === 'elevated' ? 'box-shadow:0 4px 12px rgba(0,0,0,0.15);' : '';
  const cardBorder = c.cardStyle === 'bordered' ? 'border:1px solid #e2e8f0;' : '';

  let hoverCss = '';
  if (c.hoverEffect === 'lift') hoverCss = 'transform:translateY(-4px);';
  else if (c.hoverEffect === 'scale') hoverCss = 'transform:scale(1.03);';
  else if (c.hoverEffect === 'brighten') hoverCss = 'filter:brightness(1.1);';

  const playButton = c.showPlayButton
    ? `
    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:48px;height:48px;border-radius:50%;background:${playButtonColor};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3);">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
    </div>`
    : '';

  return `
    <style>.preview-video-card:hover { ${hoverCss} }</style>
    <div style="background:${backgroundColor};padding:16px;font-family:system-ui,-apple-system,sans-serif;">
      ${
        c.showHeading
          ? `<div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;">
        ${
          c.headingStyle === 'with-icon'
            ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m10 9 5 3-5 3V9z"/></svg>'
            : ''
        }
        <h4 style="margin:0;font-size:${c.headingStyle === 'minimal' ? '0.85rem' : '1.1rem'};font-weight:${
              c.headingStyle === 'minimal' ? '500' : '600'
            };color:${c.headingStyle === 'minimal' ? '#64748b' : '#1e3a5f'};">${c.headingText || 'Related Videos'}</h4>
      </div>`
          : ''
      }
      <div style="display:flex;gap:${gap}px;overflow-x:auto;padding-bottom:8px;">
        ${sampleData.videos
          .slice(0, maxCards)
          .map(
            (video, i) => `
          <div class="preview-video-card" style="flex-shrink:0;width:${
            cardSize.width
          }px;cursor:pointer;transition:transform 0.2s;">
            <div style="position:relative;width:${cardSize.width}px;height:${
              cardSize.height
            }px;border-radius:${radius}px;overflow:hidden;background:${
              GRADIENTS[i % GRADIENTS.length]
            };${cardShadow}${cardBorder}">
              ${playButton}
              ${
                c.showDuration
                  ? `<div style="position:absolute;bottom:4px;right:4px;background:rgba(0,0,0,0.8);color:white;padding:2px 6px;border-radius:3px;font-size:0.7rem;font-weight:500;">${video.duration}</div>`
                  : ''
              }
            </div>
            <div style="padding:8px 0;">
              ${
                c.showTitle
                  ? `<div style="font-size:${cardSize.titleSize};font-weight:500;color:#1e3a5f;line-height:1.3;display:-webkit-box;-webkit-line-clamp:${titleLines};-webkit-box-orient:vertical;overflow:hidden;margin-bottom:4px;">${video.title}</div>`
                  : ''
              }
              ${
                c.showChannel || c.showViews
                  ? `<div style="font-size:${cardSize.metaSize};color:#64748b;">${c.showChannel ? video.channel : ''}${
                      c.showChannel && c.showViews ? ' • ' : ''
                    }${c.showViews ? `${video.views} views` : ''}</div>`
                  : ''
              }
            </div>
          </div>
        `
          )
          .join('')}
      </div>
    </div>`;
};

// ============================================================================
// EXPORT MODULE
// ============================================================================

export const relatedVideosCarouselTemplate: TemplateModule = {
  definition,
  Preview,
  generateHtml,
  generatePreviewHtml,
  sampleData,
};

export default relatedVideosCarouselTemplate;
